import { documentToPlainTextString } from '@contentful/rich-text-plain-text-renderer';

const BASE_URL = 'https://www.keune.com';

const LOCALE_CONFIG = {
  nl: {
    country: 'NL',
    currency: 'EUR',
    shippingRate: '6.95',
    freeShippingThreshold: 40,
    socialLinks: [
      'https://www.linkedin.com/company/keune-haircosmetics/',
      'https://twitter.com/keuneHQ',
      'https://www.instagram.com/keunehaircosmetics.nl/',
      'https://nl.pinterest.com/keunehair/',
      'https://www.tiktok.com/@keunehaircosmetics',
      'https://www.youtube.com/c/KeuneHaircosmetics',
      'https://www.facebook.com/keunehaircosmetics.nl/',
    ],
  },
  uk: {
    country: 'GB',
    currency: 'GBP',
    shippingRate: '3.95',
    freeShippingThreshold: 45,
    socialLinks: ['https://www.facebook.com/phgroupuk', 'https://www.instagram.com/keuneuk/?hl=en'],
  },
  se: {
    country: 'SE',
    currency: 'SEK',
    shippingRate: '59',
    freeShippingThreshold: 450,
    socialLinks: [
      'https://www.facebook.com/KeuneHaircosmetics/',
      'https://www.instagram.com/keunehaircosmetics',
      'https://www.linkedin.com/company/keune-haircosmetics/',
    ],
  },
  fr: {
    country: 'FR',
    currency: 'EUR',
    shippingRate: '6.95',
    freeShippingThreshold: 40,
    socialLinks: [
      'https://www.facebook.com/keunefrance/',
      'https://www.instagram.com/keunefrance/',
    ],
  },
  de: {
    country: 'DE',
    currency: 'EUR',
    shippingRate: '6.95',
    freeShippingThreshold: 40,
    socialLinks: [
      'https://www.facebook.com/keunegermany/',
      'https://www.instagram.com/keunegermany/',
    ],
  },
  es: {
    country: 'ES',
    currency: 'EUR',
    shippingRate: '4.95',
    freeShippingThreshold: 30,
    socialLinks: ['https://www.facebook.com/keuneib', 'https://www.instagram.com/keune.iberica/'],
  },
};

LOCALE_CONFIG['fr-fr'] = LOCALE_CONFIG.fr;

export const NEW_SCHEMA_LOCALES = Object.keys(LOCALE_CONFIG);

const localeBaseUrl = locale => `${BASE_URL}/${locale}`;

const getPageUrl = (slug, isProduct, locale) => {
  const cleanSlug = slug === '/' ? '' : slug;
  const prefix = isProduct ? 'p/' : '';
  return `${localeBaseUrl(locale)}/${prefix}${cleanSlug}${cleanSlug ? '/' : ''}`;
};

const getContactPoint = locale => ({
  '@type': 'ContactPoint',
  url: `${localeBaseUrl(locale)}/contact/`,
  name: 'Contact',
  email: 'hallo@keune.com',
  telephone: '+31353036036',
  '@id': `${BASE_URL}/#ContactPoint`,
});

const getPostalAddress = () => ({
  '@type': 'PostalAddress',
  streetAddress: 'Koningsweg 15',
  postalCode: '3762 EA',
  addressRegion: 'Utrecht',
  addressLocality: 'Soest',
  addressCountry: 'http://www.wikidata.org/entity/Q55',
  name: 'Postal Adress',
  '@id': `${BASE_URL}/#PostalAddress`,
});

const getWebSite = locale => ({
  '@type': 'WebSite',
  url: `${localeBaseUrl(locale)}/`,
  name: 'Website',
  potentialAction: `${localeBaseUrl(locale)}/search/#SearchAction1`,
  '@id': `${localeBaseUrl(locale)}/#WebSite`,
});

const getOrganizationBase = (includeFullDetails, locale) => {
  const base = {
    '@type': 'HealthAndBeautyBusiness',
    url: BASE_URL,
    branchCode: '3305100000',
    name: 'Organization',
    additionalProperty: 'http://www.wikidata.org/entity/Q180204',
    email: 'hallo@keune.com',
    sameAs: LOCALE_CONFIG[locale].socialLinks,
    brand: 'Keune Haircosmetics',
    foundingLocation: 'http://www.wikidata.org/entity/Q727',
    legalName: 'Keune Haircosmetics B.V.',
    areaServed: 'https://www.wikidata.org/wiki/Q13780930',
    vatID: 'NL005813141B01',
    founder: 'Jan Keune',
    '@id': `${BASE_URL}/#HealthAndBeautyBusiness`,
  };

  if (includeFullDetails) {
    return {
      ...base,
      address: getPostalAddress(),
      logo: `${BASE_URL}/#ImageObject`,
      contactPoint: { '@id': `${BASE_URL}/#ContactPoint` },
    };
  }

  return {
    ...base,
    logo: `${BASE_URL}/#ImageObject`,
    address: `${BASE_URL}/#PostalAddress`,
    contactPoint: `${BASE_URL}/#ContactPoint`,
  };
};

const getAboutPageSchema = (pageData, pageUrl, locale) => {
  const { title, description } = pageData?.seo || {};
  const headline = pageData?.title || title || '';
  const pageDescription =
    description ||
    (pageData?.description?.json ? documentToPlainTextString(pageData.description.json) : '');

  return {
    '@context': 'http://schema.org',
    '@type': 'AboutPage',
    about: {
      ...getOrganizationBase(false, locale),
      contactPoint: getContactPoint(locale),
      subjectOf: { '@id': `${localeBaseUrl(locale)}/#WebSite` },
    },
    countryOfOrigin: 'http://www.wikidata.org/entity/Q55',
    name: 'About Page',
    headline,
    url: pageUrl,
    description: pageDescription,
    isPartOf: {
      '@id': `${localeBaseUrl(locale)}/#WebSite`,
    },
    '@id': `${pageUrl}#AboutPage`,
  };
};

const getBlogPostingSchema = (pageData, pageUrl, locale) => {
  const { title, description, ogImage } = pageData?.seo || {};
  const { firstPublishedAt } = pageData?.sys || {};
  const headline = pageData?.title || title || '';

  // Find the SectionBlogArticle to get category and collaborator
  const blogSection = pageData?.sections?.find(section => section.type === 'SectionBlogArticle');
  const articleSection = blogSection?.category || '';
  const collaborator = blogSection?.collaborator;

  const schema = {
    '@context': 'http://schema.org',
    '@type': 'BlogPosting',
    articleSection,
    headline,
    url: pageUrl,
    datePublished: firstPublishedAt || '',
    name: 'Blog posting',
    image: ogImage
      ? {
          '@type': 'ImageObject',
          url: ogImage,
          name: headline,
          '@id': `${pageUrl}#ImageObject`,
        }
      : undefined,
    isPartOf: getWebSite(locale),
    publisher: {
      ...getOrganizationBase(true, locale),
    },
    '@id': `${pageUrl}#BlogPosting`,
  };

  if (collaborator) {
    const authorUrl = collaborator.slug ? getPageUrl(collaborator.slug, false, locale) : pageUrl;
    const authorDescription = collaborator.description?.json
      ? documentToPlainTextString(collaborator.description.json)
      : '';
    const authorSocials =
      collaborator.socialsCollection?.items?.map(social => social.link).filter(Boolean) || [];

    schema.author = {
      '@type': 'Person',
      url: authorUrl,
      name: collaborator.title || 'Person',
      description: authorDescription,
      sameAs: authorSocials,
      '@id': `${authorUrl}#Person`,
    };
  }

  if (description) {
    schema.description = description;
  }

  return schema;
};

const getCollectionPageSchema = (pageData, pageUrl, locale) => {
  const { title, description, keywords } = pageData?.seo || {};
  const headline = pageData?.title || title || '';
  const pageDescription =
    description ||
    (pageData?.description?.json ? documentToPlainTextString(pageData.description.json) : '');

  return {
    '@context': 'http://schema.org',
    '@type': 'CollectionPage',
    publisher: getOrganizationBase(false, locale),
    isPartOf: getWebSite(locale),
    url: pageUrl,
    headline,
    keywords: keywords || [],
    description: pageDescription,
    about: 'http://www.wikidata.org/entity/Q117207510',
    name: 'Collection Page',
    '@id': `${pageUrl}#CollectionPage`,
  };
};

const getOrganizationSchema = locale => [
  {
    '@context': 'http://schema.org',
    ...getContactPoint(locale),
  },
  {
    '@context': 'http://schema.org',
    ...getOrganizationBase(true, locale),
    subjectOf: getWebSite(locale),
  },
];

const getPersonSchema = (pageData, pageUrl, locale) => {
  const { description } = pageData?.seo || {};
  const name = pageData?.name || pageData?.title || '';
  const personDescription =
    description || (pageData?.bio?.json ? documentToPlainTextString(pageData.bio.json) : '');
  const socials = pageData?.socials?.map(social => social.link).filter(Boolean) || [];

  return {
    '@context': 'http://schema.org',
    '@type': 'Person',
    worksFor: {
      ...getOrganizationBase(false, locale),
      contactPoint: getContactPoint(locale),
      subjectOf: `${localeBaseUrl(locale)}/#WebSite`,
    },
    name: name || 'Person',
    sameAs: socials,
    url: pageUrl,
    description: personDescription,
    '@id': `${pageUrl}#Person`,
  };
};

const getBrand = () => ({
  '@type': 'Brand',
  name: 'Keune Haircosmetics',
});

const getMerchantReturnPolicy = locale => ({
  '@type': 'MerchantReturnPolicy',
  applicableCountry: LOCALE_CONFIG[locale].country,
  returnPolicyCategory: 'MerchantReturnFiniteReturnWindow',
  merchantReturnDays: 14,
  returnMethod: 'ReturnByMail',
  returnFees: 'FreeReturn',
});

const getSeller = () => ({
  '@type': 'Organization',
  name: 'Keune Haircosmetics',
});

const getPriceValidUntil = () => {
  const date = new Date();
  date.setFullYear(date.getFullYear() + 1);
  return date.toISOString().split('T')[0];
};

const getShippingDetails = (locale, price) => {
  const { country, currency, shippingRate, freeShippingThreshold } = LOCALE_CONFIG[locale];
  const shipsFree = freeShippingThreshold && Number(price) >= freeShippingThreshold;

  return {
    '@type': 'OfferShippingDetails',
    shippingDestination: {
      '@type': 'DefinedRegion',
      addressCountry: country,
    },
    shippingRate: {
      '@type': 'MonetaryAmount',
      value: shipsFree ? '0' : shippingRate,
      currency,
    },
    deliveryTime: {
      '@type': 'ShippingDeliveryTime',
      handlingTime: {
        '@type': 'QuantitativeValue',
        minValue: 0,
        maxValue: 1,
        unitCode: 'DAY',
      },
      transitTime: {
        '@type': 'QuantitativeValue',
        minValue: 1,
        maxValue: 3,
        unitCode: 'DAY',
      },
    },
  };
};

const getProductVariant = (variantData, locale) => {
  const slug = variantData?.slug || '';
  const variantUrl = getPageUrl(slug, true, locale);
  const { price, sku } = variantData?.shopifyData?.variants?.[0] || {};
  const size = variantData?.size || '';
  const image = variantData?.thumbnailImage?.url || variantData?.seo?.ogImage || '';

  const skuValue = variantData?.eanNumber || sku || '';

  const variant = {
    '@type': 'Product',
    name: size ? `${variantData?.title || ''} ${size}` : variantData?.title || '',
    sku: skuValue,
    gtin: skuValue,
    size: size || undefined,
    url: variantUrl,
    image: image || undefined,
    offers: {
      '@type': 'Offer',
      url: variantUrl,
      priceCurrency: LOCALE_CONFIG[locale].currency,
      itemCondition: 'NewCondition',
      availability: 'InStock',
      price: price ? String(price) : undefined,
      priceValidUntil: getPriceValidUntil(),
      seller: getSeller(),
      hasMerchantReturnPolicy: getMerchantReturnPolicy(locale),
      shippingDetails: getShippingDetails(locale, price),
    },
    '@id': `${variantUrl}#Product`,
  };

  return variant;
};

const getProductGroupSlug = pageData => {
  // Strip size suffix (e.g. "-300ml-2", "-1000ml") to get a clean group identifier
  const slug = pageData?.slug || '';
  return slug.replace(/-\d+ml(-\d+)?$/, '');
};

const getProductGroupId = (pageData, pageUrl, locale) => {
  const slug = getProductGroupSlug(pageData);
  if (!slug) return `${pageUrl}#ProductGroup`;
  return `${getPageUrl(slug, true, locale)}#ProductGroup`;
};

const getProductSchema = (pageData, pageUrl, locale) => {
  const name = pageData?.title || '';
  const description = pageData?.description?.json
    ? documentToPlainTextString(pageData.description.json)
    : '';
  const image = pageData?.seo?.ogImage || '';
  const variations = pageData?.productVariations || [];
  const hasVariants = variations.length > 0;
  const productGroupId = getProductGroupId(pageData, pageUrl, locale);
  const ratingData = pageData?.ratingData;

  const imageObject = image
    ? {
        '@context': 'http://schema.org',
        '@type': 'ImageObject',
        url: image,
        width: '630',
        height: '680',
        name,
        '@id': `${pageUrl}#ImageObject`,
      }
    : null;

  // Build aggregate rating if available
  const aggregateRating =
    ratingData?.average_rating != null && ratingData?.num_ratings
      ? {
          '@type': 'AggregateRating',
          ratingValue: String(parseFloat(ratingData.average_rating)),
          reviewCount: String(ratingData.num_ratings),
          bestRating: '5',
          worstRating: '1',
        }
      : undefined;

  if (hasVariants) {
    // ProductGroup schema with linked variants
    const currentVariant = getProductVariant(pageData, locale);
    const otherVariants = variations.map(v => getProductVariant(v, locale));

    const productGroup = {
      '@context': 'http://schema.org',
      '@type': 'ProductGroup',
      name,
      description,
      brand: getBrand(),
      productGroupID: getProductGroupSlug(pageData),
      hasVariant: [currentVariant, ...otherVariants],
      variesBy: 'https://schema.org/size',
      image: imageObject ? { '@id': `${pageUrl}#ImageObject` } : undefined,
      aggregateRating,
      '@id': productGroupId,
    };

    return imageObject ? [imageObject, productGroup] : [productGroup];
  }

  // Single product without variants (fallback)
  const skuValue = pageData?.eanNumber || pageData?.shopifyData?.variants?.[0]?.sku || '';
  const price = pageData?.shopifyData?.variants?.[0]?.price;

  const product = {
    '@context': 'http://schema.org',
    '@type': 'Product',
    name,
    description,
    url: pageUrl,
    brand: getBrand(),
    sku: skuValue,
    gtin: skuValue,
    size: pageData?.size || undefined,
    image: imageObject ? { '@id': `${pageUrl}#ImageObject` } : undefined,
    offers: {
      '@type': 'Offer',
      url: pageUrl,
      priceCurrency: LOCALE_CONFIG[locale].currency,
      itemCondition: 'NewCondition',
      availability: 'InStock',
      price: price ? String(price) : undefined,
      priceValidUntil: getPriceValidUntil(),
      seller: getSeller(),
      hasMerchantReturnPolicy: getMerchantReturnPolicy(locale),
      shippingDetails: getShippingDetails(locale, price),
    },
    aggregateRating,
    '@id': `${pageUrl}#Product`,
  };

  return imageObject ? [imageObject, product] : [product];
};

const getProfilePageSchema = (pageData, pageUrl, locale) => {
  const person = getPersonSchema(pageData, pageUrl, locale);

  return [
    person,
    {
      '@context': 'http://schema.org',
      '@type': 'ProfilePage',
      name: 'Profile Page',
      mainEntity: {
        '@id': `${pageUrl}#Person`,
      },
      '@id': `${pageUrl}#ProfilePage`,
    },
  ];
};

const getNewSchema = (pageData, locale = 'nl') => {
  const slug = pageData?.slug || '';
  const { schemaTypeNew } = pageData?.seo || {};
  // eslint-disable-next-line no-underscore-dangle
  const pageType = pageData?.__typename;

  let type = schemaTypeNew;
  const isProductPage = pageType === 'PageProduct' || pageType === 'PageProductBundle';

  if (!schemaTypeNew) {
    switch (pageType) {
      case 'PageProduct':
      case 'PageProductBundle':
        type = 'Product';
        break;
      case 'PageCollection':
        type = 'CollectionPage';
        break;
      case 'PageCollaborator':
        type = 'ProfilePage';
        break;
      default:
        break;
    }
  }

  const pageUrl = getPageUrl(slug, isProductPage, locale);

  switch (type) {
    case 'AboutPage':
      return getAboutPageSchema(pageData, pageUrl, locale);
    case 'BlogPosting':
      return getBlogPostingSchema(pageData, pageUrl, locale);
    case 'CollectionPage':
      return getCollectionPageSchema(pageData, pageUrl, locale);
    case 'Organization':
      return getOrganizationSchema(locale);
    case 'Product':
      return getProductSchema(pageData, pageUrl, locale);
    case 'ProfilePage':
      return getProfilePageSchema(pageData, pageUrl, locale);
    default:
      return null;
  }
};

export default getNewSchema;
